import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { ethers } from "ethers";
import axios from "axios";
import { Card, CardContent, CardMedia, Typography, Button, Grid, CircularProgress, Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton, Tooltip, Snackbar, makeStyles, Switch, FormControlLabel } from "@material-ui/core";
import { FaRegCopy } from "react-icons/fa";
import { SERVER_NAME } from './config';
import NFTManagerABI from "./E3DNFTManager_ABI.json";
import { alpha } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';

const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";

const useStyles = makeStyles((theme) => ({
  cardMediaWrapper: {
    height: 300,
    backgroundColor: '#000000',
    overflow: 'hidden',
    position: 'relative',
    cursor: 'zoom-in'
  },
  cardMedia: {
    height: '100%',
    width: '100%',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#000000',
    transition: 'transform 0.1s ease-out'
  },
  nftDescription: {
    color: '#cccccc',
    height: '4.5em', // Exactly 3 lines (1.5em per line)
    marginBottom: '10px',
    overflow: 'auto',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    '&::-webkit-scrollbar': {
      width: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#4d79ff',
      borderRadius: '4px'
    }
  },
  marketplaceContainer: {
    height: '100%', 
    backgroundColor: '#000000',
    color: '#ffffff',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    width: '100%',
    border: 'none',
  },
  header: {
    textAlign: 'center',
    marginBottom: '0px',
    marginTop: '0',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    paddingTop: '0px',
    marginTop: '0px',
    marginBottom: '10px',
    textShadow: '0 0 15px rgba(77, 121, 255, 0.8)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  subtitle: {
    fontSize: '1.25rem',
    maxWidth: '800px',
    margin: '0 auto 20px',
    color: '#cccccc',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: '10px',
    },
  },
  content: {
    padding: 0,
    margin: 0,
  },
  gridContainer: {
    padding: '0',
    margin: '0',
    width: '100%',
    '& .MuiGrid-spacing-xs-4': {
      margin: '-1px', // Negative margin to counteract the spacing
      width: 'calc(100% + 2px)',
    },
  },
  card: {
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    border: '1px solid #4d79ff !important',
    borderRadius: '15px',
    boxShadow: '0 0 0 1px #4d79ff',
    margin: '1px', /* Add minimal 1px margin for spacing */
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 8px 20px rgba(77, 121, 255, 0.3)',
    },
  },
  cardContent: {
    padding: '10px',
  },
  nftTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  nftPrice: {
    color: '#4d79ff',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  actionButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    padding: '10px 20px',
    borderRadius: '25px',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: alpha('#4d79ff', 0.8),
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1e1e1e',
      color: '#ffffff',
      borderRadius: '15px',
      border: '1px solid #4d79ff',
    },
  },
  dialogTitle: {
    backgroundColor: '#2a2a2a',
  },
  dialogContent: {
    padding: '20px',
  },
  textField: {
    '& .MuiInputBase-input': {
      color: '#ffffff',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#4d79ff',
      },
      '&:hover fieldset': {
        borderColor: '#6b8fff',
      },
    },
  },
}));

const formatAddress = (address) => {
  return address ? `${address.slice(0, 6)}...${address.slice(-6)}` : "";
};

const toHttpIpfs = (uri) => {
  const u = uri ? String(uri) : '';
  if (!u) return '';
  if (u.startsWith('ipfs://')) return u.replace('ipfs://', 'https://ipfs.io/ipfs/');
  return u;
};

const getPcapCsvUri = (nft) => {
  try {
    const props = nft && nft.properties && typeof nft.properties === 'object' ? nft.properties : null;
    const v = props && (props.pcap_csv || props.pcapCsv || props.csv) ? (props.pcap_csv || props.pcapCsv || props.csv) : null;
    return v ? String(v) : '';
  } catch (e) {
    return '';
  }
};

const getPcapFileUri = (nft) => {
  try {
    const props = nft && nft.properties && typeof nft.properties === 'object' ? nft.properties : null;
    const v = props && (props.pcap_file || props.pcapFile || props.pcap) ? (props.pcap_file || props.pcapFile || props.pcap) : null;
    return v ? String(v) : '';
  } catch (e) {
    return '';
  }
};

const E3DMarketplace = ({ walletAddress, onWalletConnect, isAuthenticated, userID }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listPrice, setListPrice] = useState("");
  const [listInE3D, setListInE3D] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [listing, setListing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [account, setAccount] = useState(null);
  const [imageScales, setImageScales] = useState({});
  const [shareSaving, setShareSaving] = useState({});
  
  // Add handler to navigate to token view when an NFT is clicked
  const handleNFTClick = (nft) => {
    navigate(`/token?nft=${nft.tokenId}`);
  };

  const handleLoadCsv = (nft, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    const pcapUri = getPcapFileUri(nft);
    if (pcapUri) {
      navigate(`/pcap?pcap=${encodeURIComponent(pcapUri)}`);
      return;
    }
    const csvUri = getPcapCsvUri(nft);
    if (csvUri) {
      navigate(`/pcap?csv=${encodeURIComponent(csvUri)}`);
    }
  };

  const handleDownloadAsset = (nft, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    const pcapUri = getPcapFileUri(nft);
    const csvUri = getPcapCsvUri(nft);
    const uri = pcapUri || csvUri;
    if (!uri) return;
    const url = toHttpIpfs(uri);
    if (!url) return;
    window.open(url, '_blank');
  };

  const setShareStatus = async (nft, nextShared, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!nft || !account) return;
    if (!window.ethereum) {
      alert('MetaMask is required to share NFTs.');
      return;
    }
    const tokenId = nft.tokenId;
    const message = `E3D Share Status\n\nToken ID: ${tokenId}\nShared: ${nextShared ? 'true' : 'false'}\nTimestamp: ${Date.now()}`;

    setShareSaving((prev) => ({ ...prev, [tokenId]: true }));
    try {
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, account],
      });
      const payload = {
        tokenId,
        shared: !!nextShared,
        walletAddress: account,
        signature,
        message,
      };

      // Some deployments route the API under /api, others strip it.
      // When running the UI on localhost but targeting the remote API (e3d.ai),
      // avoid hitting localhost-relative endpoints first (they can produce 504s
      // if no local proxy/back-end is configured).
      const isLocalServerName = (() => {
        try {
          return String(SERVER_NAME || '').includes('localhost') || String(SERVER_NAME || '').includes('127.0.0.1');
        } catch (e) {
          return false;
        }
      })();

      const candidates = isLocalServerName
        ? [
            `/nftShareStatus`,
            `/api/nftShareStatus`,
            `${SERVER_NAME}/nftShareStatus`,
            `${SERVER_NAME}/nftShareStatus`,
          ]
        : [
            `${SERVER_NAME}/nftShareStatus`,
            `${SERVER_NAME}/nftShareStatus`,
          ];

      let resp = null;
      let lastErr = null;
      let tried = [];
      for (const url of candidates) {
        try {
          tried.push(url);
          resp = await axios.post(url, payload, { withCredentials: false });
          if (resp && resp.data) break;
        } catch (e2) {
          lastErr = e2;
        }
      }

      if (resp && resp.data && resp.data.success) {
        setNfts((prev) => prev.map((x) => (x.tokenId === tokenId ? { ...x, shared: !!nextShared } : x)));
      } else {
        const serverErr = resp && resp.data && resp.data.error ? String(resp.data.error) : '';
        const status = lastErr && lastErr.response && typeof lastErr.response.status !== 'undefined'
          ? String(lastErr.response.status)
          : '';
        const data = lastErr && lastErr.response && lastErr.response.data ? lastErr.response.data : null;
        const code = lastErr && lastErr.code ? String(lastErr.code) : '';
        const msg = serverErr || (lastErr && lastErr.message ? String(lastErr.message) : 'Failed to update share status');
        console.error('Failed to set share status:', { tokenId, nextShared, tried, resp: resp && resp.data, error: lastErr });
        alert(`${msg}${status ? `\nHTTP: ${status}` : ''}${code ? `\nCode: ${code}` : ''}${tried.length ? `\nTried: ${tried.join(', ')}` : ''}${data ? `\nResponse: ${typeof data === 'string' ? data : JSON.stringify(data)}` : ''}`);
      }
    } catch (err) {
      const serverErr = err && err.response && err.response.data && err.response.data.error
        ? String(err.response.data.error)
        : '';
      const status = err && err.response && typeof err.response.status !== 'undefined'
        ? String(err.response.status)
        : '';
      const data = err && err.response && err.response.data ? err.response.data : null;
      const code = err && err.code ? String(err.code) : '';
      const msg = serverErr || (err && err.message ? String(err.message) : 'Failed to update share status');
      console.error('Error setting share status:', err);
      alert(`${msg}${status ? `\nHTTP: ${status}` : ''}${code ? `\nCode: ${code}` : ''}${data ? `\nResponse: ${typeof data === 'string' ? data : JSON.stringify(data)}` : ''}`);
    } finally {
      setShareSaving((prev) => ({ ...prev, [tokenId]: false }));
    }
  };

  const marketplaceContainerRef = useRef(null);

  useEffect(() => {
    fetchListedNFTs();
  
    const handleAccountsChanged = (accounts) => {
      if (accounts.length > 0) {
        fetchListedNFTs();
      } 
    };
  
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
    }
  
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);

  const handleMarketplaceResize = () => {
    console.log("First render complete");
    console.log("Ref after first render:", marketplaceContainerRef);
    console.log("Ref.current after first render:", marketplaceContainerRef?.current);
    
    if (!marketplaceContainerRef.current) return;

    const e3dHeader = document.getElementById('e3dHeader');
    const e3dHeaderHeight = e3dHeader ? e3dHeader.clientHeight : 0;
    const adBannerContainer = document.getElementById('adBannerContainer');
    const adBannerContainerHeight = adBannerContainer ? adBannerContainer.clientHeight : 0;
    const premiumBannerContainer = document.getElementById('premiumBannerContainer');
    const premiumBannerContainerHeight = premiumBannerContainer ? premiumBannerContainer.clientHeight : 0;
    const otherHeight = e3dHeaderHeight + adBannerContainerHeight + premiumBannerContainerHeight;

    const appContainer = document.getElementById('appContainer');
    if (!appContainer) return;
    const appSize = appContainer.getBoundingClientRect();
    const height = appSize.height;

    console.log(`App container width and height: ${appSize.width} x ${appSize.height}`);

    const marketplaceContainer = marketplaceContainerRef.current;
    if (marketplaceContainer) {
      const marketplaceHeight = height - otherHeight;
      marketplaceContainer.style.height = `${marketplaceHeight}px`; 
      marketplaceContainer.style.maxHeight = `${marketplaceHeight}px`; 
      marketplaceContainer.style.scrollHeight = `${marketplaceHeight}px`; 
    }
  };
  
  useEffect(() => {
    // Manually set the ref to the DOM element
    const manuallySetRef = () => {
      const element = document.getElementById('marketplaceContainer');
      if (element && !marketplaceContainerRef.current) {
        console.log("Manually setting ref to:", element);
        marketplaceContainerRef.current = element;
        
        // Now trigger your resize function since the ref is set
        handleMarketplaceResize();
      }
    };
    
    // Try immediately
    manuallySetRef();
        
    return () => {};
  }, [loading]);
  
  useLayoutEffect(() => {
    window.addEventListener('resize', handleMarketplaceResize);
    handleMarketplaceResize();

    return () => {
      window.removeEventListener('resize', handleMarketplaceResize);
    };
  }, [loading]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
    }).catch(err => {
      console.error("Could not copy text: ", err);
    });
  };

  const handleBuyOrManage = async (nft) => {
    if (!account) {
      alert("Connect your wallet first.");
      return;
    }

    if (nft.owner.toLowerCase() === account.toLowerCase()) {
      if (nft.isListed) {
        await unlistNFT(nft.tokenId);
      } else {
        setSelectedTokenId(nft.tokenId);
        setListDialogOpen(true);
      }
    } else {
      await buyNFT(nft);
    }
  };

  const unlistNFT = async (tokenId) => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);
      const tx = await nftContract.unlistNFT(tokenId);
      await tx.wait();
      alert("NFT Unlisted Successfully!");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error unlisting NFT:", error);
      alert("Unlisting failed.");
    }
  };

  const listNFT = async () => {
    try {
      setListing(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);

      const amountInWei = ethers.utils.parseUnits(listPrice, 18);
      const tx = await nftContract.listNFT(selectedTokenId, amountInWei, listInE3D);
      await tx.wait();

      setListDialogOpen(false);
      setListError("");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error listing NFT:", error);
      setListError("Failed to list NFT. Please try again.");
    } finally {
      setListing(false);
    }
  };

  const buyNFT = async (nft) => {
    try {
      setBuyingId(nft.tokenId);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);
      const e3dToken = new ethers.Contract(E3DToken_ADDRESS, [
        "function approve(address spender, uint256 amount) external returns (bool)",
        "function allowance(address owner, address spender) external view returns (uint256)"
      ], signer);

      if (nft.currency === "E3D") {
        const amountInWei = ethers.utils.parseUnits(nft.price.toString(), 18);
        const userAddress = await signer.getAddress();
        const allowance = await e3dToken.allowance(userAddress, NFTManager_ADDRESS);
  
        if (allowance.lt(amountInWei)) {
          const approveTx = await e3dToken.approve(NFTManager_ADDRESS, amountInWei);
          await approveTx.wait();
        }
      }

      const tx = await nftContract.buyNFT(nft.tokenId, nft.currency === "E3D" ? true : false);
      await tx.wait();

      //alert("NFT Purchased Successfully!");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error buying NFT:", error);
      alert("Purchase failed.");
    } finally {
      setBuyingId(null);
    }
  };

  const fetchListedNFTs = async () => {
    try {
      setLoading(true);
  
      const { ethereum } = window;
  
      if (ethereum) {
        console.log("fetchListedNFTs: metamask is installed")
        const accounts = await ethereum.request({ method: 'eth_accounts' });
  
        if (accounts.length > 0) {
          console.log("fetchListedNFTs: found metamask account")

          // ✅ MetaMask is installed AND connected
          const provider = new ethers.providers.Web3Provider(ethereum);
          const signer = provider.getSigner();
          const address = await signer.getAddress();
          setAccount(address);
  
          const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, provider);
          const fetchedNFTs = [];
          let i = 1;
  
          while (true) {
            try {
              const uri = await nftContract.tokenURI(i);
              if (!uri) break;
  
              let ipfsUri = uri;
              if (uri.startsWith("ipfs://")) {
                ipfsUri = uri.replace("ipfs://", "https://ipfs.io/ipfs/");
              }
  
              const response = await axios.get(ipfsUri, { withCredentials: false });
              const metadata = response.data;
  
              const priceInWei = await nftContract.nftPrices(i);
              const isPriceInE3D = await nftContract.isPriceInE3D(i);
              const owner = await nftContract.ownerOf(i);

              const priceInE3D = ethers.utils.formatUnits(priceInWei, 18);

              fetchedNFTs.push({
                tokenId: i,
                price: priceInE3D,
                currency: isPriceInE3D ? 'E3D' : 'ETH',
                isListed: !priceInWei.isZero(),
                owner,
                ...metadata
              });

              i++;
            } catch (error) {
              break;
            }
          }

          // Merge share status for direct chain fetch (server fallback already includes it)
          try {
            const tokenIds = fetchedNFTs.map((n) => n.tokenId).join(',');
            let shareResp = null;
            try {
              shareResp = await axios.get(
                `${SERVER_NAME}/nftShareStatus/bulk?tokenIds=${encodeURIComponent(tokenIds)}`,
                { withCredentials: false }
              );
            } catch (e1) {
              shareResp = await axios.get(
                `${SERVER_NAME}/nftShareStatus/bulk?tokenIds=${encodeURIComponent(tokenIds)}`,
                { withCredentials: false }
              );
            }

            const items = shareResp && shareResp.data && shareResp.data.items ? shareResp.data.items : {};
            for (let j = 0; j < fetchedNFTs.length; j += 1) {
              const tid = String(fetchedNFTs[j].tokenId);
              if (items[tid] && typeof items[tid].shared !== 'undefined') {
                fetchedNFTs[j].shared = !!items[tid].shared;
              }
            }
          } catch (e) {
          }

          setNfts(fetchedNFTs);
        } else {
          // ❌ MetaMask installed, but NOT connected
          console.log("MetaMask installed but no connected account. Falling back to server.");
          const response = await axios.get(`${SERVER_NAME}/fetchNFTs/`);
          const fetchedNFTs = response.data.nfts || [];
          setNfts(fetchedNFTs);
        }

      } else {
        // ❌ MetaMask not installed
        console.log("MetaMask not installed. Falling back to server.");
        const response = await axios.get(`${SERVER_NAME}/fetchNFTs/`);
        const fetchedNFTs = response.data.nfts || [];
        setNfts(fetchedNFTs);
      }
  
    } catch (error) {
      console.error("Error fetching NFTs:", error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return (
    <div className={classes.loadingContainer}>
      <CircularProgress style={{ color: '#4d79ff' }} size={60} />
    </div>
  );

  return (
    <div ref={marketplaceContainerRef} id='marketplaceContainer' className={classes.marketplaceContainer}>
      <div className={classes.content}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: '#4d79ff' }} size={60} />
          </div>
        ) : (
          <Grid container spacing={0} className={classes.gridContainer}>
            {nfts
              .filter((nft) => {
                const isOwner = account && nft && nft.owner && String(nft.owner).toLowerCase() === String(account).toLowerCase();
                const isShared = !!(nft && nft.shared);
                const isListed = !!(nft && nft.isListed);
                return isOwner || isShared || isListed;
              })
              .map((nft) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={nft.tokenId}>
                <Card 
                className={classes.card} 
                elevation={0}
                onClick={() => handleNFTClick(nft)}
                style={{ cursor: 'pointer' }}
              >
                <div 
                  className={classes.cardMediaWrapper}
                  onWheel={(e) => {
                    e.preventDefault();
                    const scale = imageScales[nft.tokenId] || 1;
                    const newScale = scale + (e.deltaY > 0 ? -0.1 : 0.1);
                    const clampedScale = Math.min(Math.max(newScale, 0.5), 3);
                    setImageScales(prev => ({ ...prev, [nft.tokenId]: clampedScale }));
                  }}
                >
                  <CardMedia
                    className={classes.cardMedia}
                    image={toHttpIpfs(nft.image)}
                    title={`NFT ${nft.tokenId}`}
                    style={{
                      transform: `scale(${imageScales[nft.tokenId] || 1})`
                    }}
                  />
                </div>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.nftTitle} gutterBottom>
                    {nft.name || "Unnamed NFT"} (#{nft.tokenId})
                  </Typography>
                  <Typography 
                    variant="body2" 
                    className={classes.nftDescription}
                  >
                    {nft.description}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <Typography variant="body2" style={{ color: '#cccccc' }}>
                      Owner: {formatAddress(nft.owner)}
                    </Typography>
                    <Tooltip title="Copy Address" placement="top">
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(nft.owner)}
                        style={{ color: '#4d79ff', marginLeft: '4px' }}
                      >
                        <FaRegCopy />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <Typography className={classes.nftPrice} style={{ minHeight: '24px' }}>
                    {nft.isListed ? `${nft.price} E3D` : ''}
                  </Typography>

                  {(() => {
                    const isOwner = account && nft && nft.owner && String(nft.owner).toLowerCase() === String(account).toLowerCase();
                    const pcapUri = getPcapFileUri(nft);
                    const csvUri = getPcapCsvUri(nft);
                    const hasAny = !!(pcapUri || csvUri);
                    const canLoad = !!(hasAny && nft.shared && !nft.isListed);
                    const showActions = !isOwner ? canLoad : hasAny;
                    const disabled = isOwner ? false : !canLoad;
                    const labelOpen = pcapUri ? 'Open Graph' : 'Open Graph';
                    const labelDownload = pcapUri ? 'Download PCAP' : 'Download CSV';
                    return showActions ? (
                      <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Button
                          className={classes.actionButton}
                          fullWidth
                          onClick={(e) => handleLoadCsv(nft, e)}
                          disabled={disabled}
                          style={{ backgroundColor: disabled ? '#353535' : alpha('#4d79ff', 0.9) }}
                        >
                          {labelOpen}
                        </Button>
                        <Button
                          className={classes.actionButton}
                          fullWidth
                          onClick={(e) => handleDownloadAsset(nft, e)}
                          disabled={disabled}
                          style={{ backgroundColor: disabled ? '#353535' : alpha('#4d79ff', 0.65) }}
                        >
                          {labelDownload}
                        </Button>
                      </div>
                    ) : null;
                  })()}

                  {(() => {
                    const isOwner = account && nft && nft.owner && String(nft.owner).toLowerCase() === String(account).toLowerCase();
                    if (!isOwner) return null;
                    const busy = !!shareSaving[nft.tokenId];
                    return (
                      <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!nft.shared}
                              onChange={(e) => setShareStatus(nft, e.target.checked, e)}
                              color="primary"
                              disabled={busy}
                            />
                          }
                          label={busy ? 'Saving…' : (nft.shared ? 'Shared' : 'Not Shared')}
                          style={{ color: '#cccccc' }}
                        />
                      </div>
                    );
                  })()}

                  <Button
                    className={classes.actionButton}
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click event when button is clicked
                      handleBuyOrManage(nft);
                    }}
                    disabled={listing || (buyingId === nft.tokenId)}
                    style={{ marginTop: '15px' }}
                  >
                    {buyingId === nft.tokenId ? (
                      <CircularProgress size={24} style={{ color: '#ffffff' }} />
                    ) : nft.owner.toLowerCase() === account?.toLowerCase() ? (
                      nft.isListed ? "Unlist NFT" : "List for Sale"
                    ) : (
                      nft.isListed ? "Buy Now" : "Not Listed"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      </div>
      
      <Dialog 
        open={listDialogOpen} 
        onClose={() => setListDialogOpen(false)}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          List NFT for Sale
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            className={classes.textField}
            autoFocus
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            value={listPrice}
            onChange={(e) => setListPrice(e.target.value)}
            error={!!listError}
            helperText={listError}
            variant="outlined"
          />
          <Button
            className={classes.actionButton}
            onClick={() => setListInE3D(!listInE3D)}
            style={{ marginTop: '20px' }}
            fullWidth
          >
            Price in: {listInE3D ? "E3D" : "ETH"}
          </Button>
        </DialogContent>
        <DialogActions style={{ padding: '20px' }}>
          <Button 
            onClick={() => setListDialogOpen(false)} 
            style={{ color: '#cccccc' }}
          >
            Cancel
          </Button>
          <Button 
            className={classes.actionButton}
            onClick={listNFT} 
            disabled={listing}
          >
            {listing ? <CircularProgress size={24} style={{ color: '#ffffff' }} /> : "List NFT"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Address copied to clipboard"
        ContentProps={{
          style: {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            border: '1px solid #4d79ff',
          }
        }}
      />
    </div>
  );
};

export default E3DMarketplace;
